// src/config/database/sfJobTypesConfig.js

export const jobTypesTableConfig = {
  // Table identification
  id: 'sf_job_types',
  name: 'Small Format Job Types',
  apiEndpoint: '/api/admin/small-format/job-types',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'id',
      label: 'ID',
      sortable: true,
      render: (value) => (
        <span className="font-mono text-sm text-gray-600">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700 truncate max-w-xs" title={value}>
          {value || '-'}
        </span>
      )
    },
    {
      key: 'double_sided_default',
      label: 'Double Sided Default',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
        }`}>
          {value ? 'Yes' : 'No'}
        </span>
      )
    },
    {
      key: 'multiplier',
      label: 'Multiplier',
      sortable: true,
      render: (value) => (
        <span className="text-gray-900">
          {value ? `${parseFloat(value).toFixed(2)}x` : '1.00x'}
        </span>
      )
    },
    {
      key: 'default_finishing',
      label: 'Default Finishing',
      sortable: false,
      render: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return <span className="text-gray-400">-</span>;
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {value.slice(0, 2).map((item, index) => (
              <span 
                key={index}
                className="inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800"
              >
                {item}
              </span>
            ))}
            {value.length > 2 && (
              <span className="text-xs text-gray-500">+{value.length - 2} more</span>
            )}
          </div>
        );
      }
    }
  ],

  // Form field definitions for the RecordModal
  fields: [
    {
      key: 'name',
      label: 'Job Type Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Business Cards, Flyers, Booklets',
      help: 'A descriptive name for this job type'
    },
    {
      key: 'description',
      label: 'Description',
      type: 'textarea',
      required: false,
      placeholder: 'Brief description of this job type...',
      rows: 3,
      help: 'Optional description to help users understand this job type'
    },
    {
      key: 'double_sided_default',
      label: 'Double Sided by Default',
      type: 'checkbox',
      required: false,
      defaultValue: false,
      help: 'Check if this job type should default to double-sided printing'
    },
    {
      key: 'default_paper_stock',
      label: 'Default Paper Stock ID',
      type: 'text',
      required: false,
      placeholder: 'e.g., a4-80gsm-white',
      help: 'The default paper stock ID for this job type (optional)'
    },
    {
      key: 'default_printer',
      label: 'Default Printer ID',
      type: 'text',
      required: false,
      placeholder: 'e.g., colour, mono',
      help: 'The default printer ID for this job type (optional)'
    },
    {
      key: 'default_finishing',
      label: 'Default Finishing Processes',
      type: 'multiselect',
      required: false,
      options: [], // Will be populated dynamically from finishing processes
      defaultValue: [],
      help: 'Finishing processes that will be automatically selected for this job type',
      placeholder: 'Select default finishing processes...'
    },
    {
      key: 'multiplier',
      label: 'Price Multiplier',
      type: 'number',
      required: false,
      defaultValue: 1.0,
      min: 0.1,
      max: 10.0,
      step: 0.1,
      placeholder: '1.0',
      help: 'Multiplier applied to calculated prices (1.0 = no change)',
      validate: (value) => {
        const num = parseFloat(value);
        if (value && (isNaN(num) || num <= 0)) {
          return 'Multiplier must be a positive number';
        }
        return null;
      }
    }
  ],

  // Validation rules
  validation: {
    uniqueFields: ['name'], // These fields must be unique across records
    
    // Custom validation function
    validateRecord: (data, isEdit = false) => {
      const errors = {};
      
      // Validate that name doesn't contain special characters that might break IDs
      if (data.name) {
        const nameRegex = /^[a-zA-Z0-9\s\-_()]+$/;
        if (!nameRegex.test(data.name)) {
          errors.name = 'Name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses';
        }
      }
      
      return errors;
    }
  },

  // Relationship warnings for delete operations
  relationships: [
    {
      description: 'saved quotes',
      checkEndpoint: '/api/admin/check-job-type-usage', // This would need to be implemented
      warningMessage: (count) => `This job type is used by ${count} saved quotes`
    }
  ],

  // Search configuration
  searchable: true,
  searchFields: ['name', 'description'], // Fields to search in

  // Default sorting
  defaultSort: { key: 'name', direction: 'asc' },

  // Bulk operations
  bulkOperations: [
    {
      id: 'export',
      label: 'Export Selected',
      icon: 'download',
      action: 'export',
      confirmMessage: null // No confirmation needed for export
    },
    {
      id: 'delete',
      label: 'Delete Selected',
      icon: 'trash',
      action: 'delete',
      confirmMessage: (count) => `Are you sure you want to delete ${count} job type${count !== 1 ? 's' : ''}? This action cannot be undone.`,
      dangerous: true
    }
  ]
};

// Helper function to transform data for API calls
export const transformJobTypeForApi = (formData, isEdit = false) => {
  const transformed = { ...formData };
  
  // Ensure ID is generated from name for new records
  if (!isEdit && transformed.name) {
    transformed.id = transformed.name
      .toLowerCase()
      .replace(/[^a-z0-9\s\-_]/g, '') // Remove special chars except spaces, hyphens, underscores
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  }
  
  // Handle default_finishing field - ensure it's an array
  if (transformed.default_finishing) {
    if (typeof transformed.default_finishing === 'string') {
      try {
        // Split semicolon-separated values and convert to array
        transformed.default_finishing = transformed.default_finishing
          .split(';')
          .map(f => f.trim().toLowerCase().replace(/\s+/g, '-'))
          .filter(f => f.length > 0);
      } catch (e) {
        transformed.default_finishing = [];
      }
    } else if (Array.isArray(transformed.default_finishing)) {
      // Already an array, ensure all values are properly formatted
      transformed.default_finishing = transformed.default_finishing
        .map(f => typeof f === 'string' ? f.trim().toLowerCase().replace(/\s+/g, '-') : f)
        .filter(f => f && f.length > 0);
    } else {
      transformed.default_finishing = [];
    }
  } else {
    transformed.default_finishing = [];
  }
  
  // Ensure multiplier is a number
  if (transformed.multiplier) {
    transformed.multiplier = parseFloat(transformed.multiplier);
  }
  
  return transformed;
};

// Helper function to transform data from API for display
export const transformJobTypeFromApi = (apiData) => {
  const transformed = { ...apiData };
  
  // Ensure default_finishing is an array for the multiselect field
  if (transformed.default_finishing) {
    if (typeof transformed.default_finishing === 'string') {
      try {
        // Try to parse as JSON first (this should work for your data format)
        transformed.default_finishing = JSON.parse(transformed.default_finishing);
      } catch (e) {
        // Fall back to comma-separated parsing (in case there are legacy formats)
        transformed.default_finishing = transformed.default_finishing
          .split(',')
          .map(f => f.trim())
          .filter(f => f.length > 0);
      }
    }
    // If it's already an array, keep it as is
    if (!Array.isArray(transformed.default_finishing)) {
      transformed.default_finishing = [];
    }
  } else {
    transformed.default_finishing = [];
  }
  
  return transformed;
};